import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Dialog,
  CircularProgress,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import MaterialTable from "../../components/Table/MaterialTable";
import HotelAmenityMappingForm from "./HotelAmenityMappingForm";
import * as XLSX from "xlsx"; // ✅ Import xlsx for Excel export

const HotelAmenityMapping = () => {
  const [tableData, setTableData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    type: "success",
  });
  const [hotels, setHotels] = useState([]);

  const columns = [
    { title: "Hotel ID", field: "hotel_id", type: "numeric" },
    { title: "Amenity ID", field: "amenitie_id", type: "numeric" },
    { title: "Room Type", field: "room_type" },
    { title: "Activity Type", field: "activity_type" },
    {
      title: "Status",
      field: "status",
      render: (rowData) => (
        <span
          style={{
            backgroundColor: rowData.status === "1" ? "#e6f4ea" : "#fce8e6",
            color: rowData.status === "1" ? "#1e4620" : "#c5221f",
            padding: "4px 8px",
            borderRadius: "4px",
          }}
        >
          {rowData.status === "1" ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      title: "Created At",
      field: "created_at",
      render: (rowData) => new Date(rowData.created_at).toLocaleString(),
    },
    {
      title: "Updated At",
      field: "updated_at",
      render: (rowData) => new Date(rowData.updated_at).toLocaleString(),
    },
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch("https://api.esthell.com/hm_map_amenities");
      if (!response.ok) throw new Error("Failed to fetch data");
      const responseData = await response.json();
      setTableData(Array.isArray(responseData.data) ? responseData.data : []);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const fetchHotels = async () => {
    try {
      const response = await fetch("https://api.esthell.com/Hotel");
      const data = await response.json();
      if (data.success) {
        setHotels(data.data);
      }
    } catch (error) {
      console.error("Error fetching hotels:", error);
    }
  };

  useEffect(() => {
    fetchHotels();
    fetchData();
  }, []);
  const handleExportToExcel = () => {
    const formattedData = tableData.map((row) => ({
      "Hotel ID": row.hotel_id,
      "Amenity ID": row.amenitie_id,
      "Room Type": row.room_type,
      "Activity Type": row.activity_type,
      Status: row.status === "1" ? "Active" : "Inactive",
      "Created At": new Date(row.created_at).toLocaleString(),
      "Updated At": new Date(row.updated_at).toLocaleString(),
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "AmenityMappings");
    XLSX.writeFile(workbook, "HotelAmenityMappings.xlsx");
  };
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleFormSubmit = async (formData) => {
    try {
      const url = editData
        ? `https://api.esthell.com/hm_map_amenities/${editData.id}`
        : "https://api.esthell.com/create_map_amenities";

      const method = editData ? "PUT" : "POST";

      // Format the payload according to the API requirements
      const payload = editData
        ? { data: formData } // For update, wrap in data object
        : formData; // For create, send as is

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const responseData = await response.json();

      if (!responseData.success) {
        throw new Error(responseData.message || "Operation failed");
      }

      const message = editData
        ? "Amenity mapping updated successfully!"
        : "Amenity mapping created successfully!";

      setNotification({ open: true, message, type: "success" });
      setShowForm(false);
      setEditData(null);
      fetchData();
    } catch (err) {
      setError(err.message);
      setNotification({ open: true, message: err.message, type: "error" });
    }
  };

  const handleEdit = (row) => {
    // Make a copy of the row data to avoid direct mutation
    const rowData = { ...row };

    // Ensure hotel_id is a string for the form select
    if (rowData.hotel_id && typeof rowData.hotel_id === "number") {
      rowData.hotel_id = rowData.hotel_id.toString();
    }

    // Ensure amenitie_id is a string for the form select
    if (rowData.amenitie_id && typeof rowData.amenitie_id === "number") {
      rowData.amenitie_id = rowData.amenitie_id.toString();
    }

    // Ensure room_type is properly set
    if (!rowData.room_type) {
      rowData.room_type = "";
    }

    // Ensure activity_type is properly set
    if (!rowData.activity_type) {
      rowData.activity_type = "";
    }

    // Ensure status is a string for the form select
    if (rowData.status && typeof rowData.status === "number") {
      rowData.status = rowData.status.toString();
    }

    console.log("Editing row data:", rowData); // Debug log
    setEditData(rowData);
    setShowForm(true);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h4" sx={{ color: "#1a237e", fontWeight: "bold" }}>
          Hotel Amenity Mapping
        </Typography>
        <Button variant="outlined" onClick={handleExportToExcel}>
          Export to Excel
        </Button>
      </Box>

      {loading ? (
        <CircularProgress />
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <MaterialTable
          columns={columns}
          data={tableData}
          onEdit={handleEdit}
          selection={false}
          actions={false}
          toolbar={{
            create: true,
            onCreateClick: () => {
              setEditData(null);
              setShowForm(true);
            },
          }}
        />
      )}

      <Dialog
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setEditData(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "90vh", overflowY: "auto" } }}
      >
        <Box sx={{ p: 3 }}>
          <HotelAmenityMappingForm
            handleClose={() => {
              setShowForm(false);
              setEditData(null);
            }}
            onSubmit={handleFormSubmit}
            initialData={editData}
            hotels={hotels}
          />
        </Box>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.type}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HotelAmenityMapping;
